import { Controller } from '@hotwired/stimulus'
import { DateTime } from 'luxon'
import { DateTimePicker, DateTimePickerEvents } from '@/utils/date_time_picker'
import { DateTime as TempusDominusDateTime } from '@eonasdan/tempus-dominus'

export default class extends Controller {
  static targets = ['element', 'offHoursContainer', 'input', 'now']
  static values = {
    defaultDate: String,
    minDate: String,
    maxDate: String,
    inputType: { type: String, default: 'datetime' }, // datetime, date, or time
    additionalOptions: { type: Object, default: {} }, // Allows us to add additional options to select
    onHoursStartTime: { type: Number, default: 8 }, // Determines when on hours begin, 8:00am
    onHoursEndTime: { type: Number, default: 19 }, // Determines when on hours end, 7:00pm
    showYear: { type: Boolean, default: true },
    submitOnChange: { type: Boolean, default: false },
    format: String,
    inline: Boolean,
    submitOnNow: Boolean,
  }

  #format = 'MM/dd/yyyy h:mm a' // Default format for datetime fields

  #eventTypes = Object.keys(DateTimePickerEvents)

  get parentFormElement() {
    // Search up the DOM tree for the first parent element that is a form
    return this.element.closest('form')
  }

  get defaultOptions() {
    return {
      useCurrent: true,
      restrictions: {
        minDate:
          this.inputTypeValue === 'date' && !this.showYearValue
            ? new Date(new Date().getFullYear(), 0, 1)
            : undefined,
        maxDate:
          this.inputTypeValue === 'date' && !this.showYearValue
            ? new Date(new Date().getFullYear(), 11, 31)
            : undefined,
      },
      display: {
        inline: this.inlineValue,
        components: {
          calendar: this.inputTypeValue !== 'time',
          clock: this.inputTypeValue !== 'date',
          year: this.showYearValue,
          decades: this.showYearValue,
        },
      },
    }
  }

  get mergedOptions() {
    const options = {
      ...this.defaultOptions,
      ...this.additionalOptionsValue,
    }

    const parseStringDate = (stringDate) => {
      return DateTime.fromISO(stringDate).toJSDate()
    }

    if (this.hasMinDateValue) {
      options.restrictions.minDate = parseStringDate(this.minDateValue)
    }

    if (this.hasMaxDateValue) {
      options.restrictions.maxDate = parseStringDate(this.maxDateValue)
    }

    if (this.hasDefaultDateValue) {
      options.defaultDate = parseStringDate(this.defaultDateValue)
      options.viewDate = parseStringDate(this.defaultDateValue)
    }

    return options
  }

  connect() {
    if (!this.hasElementTarget || this.connected) {
      return
    }

    // do not reconnect if element is moved in the DOM. The date picker only needs
    // to be initialized one time
    this.connected = true

    if (this.formatValue) {
      this.#format = this.formatValue
    } else if (this.inputTypeValue === 'date' && !this.showYearValue) {
      this.#format = 'MMMM d'
    } else if (this.inputTypeValue === 'date') {
      this.#format = 'MM/dd/yyyy'
    } else if (this.inputTypeValue === 'time') {
      this.#format = 'h:mm a'
    }

    this.picker = new DateTimePicker(this.elementTarget, {
      ...this.mergedOptions,
      meta: {
        parseInput: (stringDate) => {
          // If the input field isn't required, allow it to be cleared
          return this.elementTarget.required || stringDate
            ? DateTime.fromFormat(stringDate, this.#format)
            : null
        },

        formatInput: (date) => {
          // Parse to luxon DateTime
          const dateTime = DateTime.fromJSDate(date)

          if (!dateTime.isValid) {
            return ''
          }

          if (this.hasOffHoursContainerTarget) {
            // Update UI if date is in off hours
            this.checkIfOffHours(dateTime)
          }

          // Format to date to save to input field
          return dateTime.toFormat(this.#format)
        },
      },
    })

    this.picker.subscribe(
      this.#eventTypes.map((typeKey) => DateTimePickerEvents[typeKey]),
      this.#eventTypes.map((eventType) => (event) => {
        this.elementTarget.dispatchEvent(
          new CustomEvent(`date-picker:${eventType}`, {
            detail: event,
            bubbles: true,
          }),
        )
      }),
    )

    // if the date picker is inline and there is an input target, update the target
    // on change
    if (this.inlineValue && this.hasInputTarget) {
      this.picker.subscribe(DateTimePickerEvents.change, (event) => {
        this.inputTarget.value = this.picker.dates.formatInput(event.date)
      })
    }

    // if we want to submit the form on change, we will set up an event listener here
    if (this.submitOnChangeValue) {
      this.picker.subscribe(DateTimePickerEvents.change, (event) => {
        this.element.submit()
      })
    }

    // if there is a now target, listen for clicks to reset the date picker
    if (this.hasNowTarget) {
      this.nowTarget.addEventListener(
        'click',
        function (event) {
          const now = new TempusDominusDateTime()

          // update the date picker
          this.picker.dates.setValue(now)
          this.picker.viewDate = now

          // update the form input if it is separate
          if (this.hasInputTarget) {
            this.inputTarget.value = this.picker.dates.formatInput(now)
          }

          // submit the form if the now button should trigger a submit
          if (this.submitOnNowValue) {
            this.element.submit()
          }
        }.bind(this),
      )
    }
  }

  // check if date selected is in off hours, if it is display warning container
  checkIfOffHours(date) {
    const selectedHour = date.hour
    // if no time has been selected return
    if (!selectedHour && selectedHour != 0) {
      return
    }

    // if selected hour is before offHourStartTime and after offHoursEndTime
    // display warning
    if (
      selectedHour < this.onHoursStartTimeValue ||
      selectedHour > this.onHoursEndTimeValue
    ) {
      this.offHoursContainerTarget.style.display = ''
    } else {
      this.offHoursContainerTarget.style.display = 'none'
    }
  }
}
